import { BellRing, Check } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  useCommunications,
  useMarkCommunicationRead,
  useMyOffer,
} from "@/features/rfq/api/proceedings";

/**
 * Wyróżniony baner na SAMEJ GÓRZE strony postępowania dla oferenta.
 *
 * Pokazuje się TYLKO firmom, które już wystartowały (złożyły ofertę) i mają
 * nieprzeczytane komunikaty — zwłaszcza ogłoszenia o zmianie warunków. Oferent musi
 * kliknąć „Oznacz jako przeczytane"; GK trzyma w logach, kto i kiedy potwierdził.
 */
export function UnreadCommunicationsBanner({
  proceedingId,
}: {
  proceedingId: string;
}) {
  const { data: offer } = useMyOffer(proceedingId);
  const { data: communications } = useCommunications(proceedingId);
  const markRead = useMarkCommunicationRead(proceedingId);

  // Potwierdzać odczyt ma sens tylko dla firm, które faktycznie wystartowały.
  if (!offer || offer.status !== "submitted") return null;
  const unread = (communications ?? []).filter((c) => !c.read);
  if (unread.length === 0) return null;

  return (
    <div className="mb-6 overflow-hidden rounded-lg border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-amber-100 shadow-lg ring-2 ring-amber-200 dark:border-amber-500/60 dark:from-amber-950/50 dark:to-amber-900/30 dark:ring-amber-900/40">
      <div className="flex items-center gap-2 border-b border-amber-300/70 bg-amber-200/50 px-4 py-2 dark:border-amber-700/50 dark:bg-amber-900/30">
        <BellRing size={16} className="animate-pulse text-amber-700 dark:text-amber-300" />
        <span className="text-sm font-bold uppercase tracking-wide text-amber-900 dark:text-amber-200">
          Nowe komunikaty do potwierdzenia ({unread.length})
        </span>
      </div>
      <ul className="divide-y divide-amber-300/50 dark:divide-amber-800/40">
        {unread.map((c) => (
          <li key={c.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-950 dark:text-amber-100">
                {c.title}
                {c.is_change && (
                  <span className="rounded bg-amber-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                    Zmiana warunków
                  </span>
                )}
              </p>
              <p className="mt-0.5 whitespace-pre-wrap text-sm text-amber-900/90 dark:text-amber-200/90">
                {c.body}
              </p>
              {c.extended_deadline_to && (
                <p className="mt-1 text-xs font-medium text-amber-800 dark:text-amber-300">
                  Termin składania przesunięty na{" "}
                  {new Date(c.extended_deadline_to).toLocaleString("pl")}.
                </p>
              )}
            </div>
            <Button
              size="sm"
              onClick={() => markRead.mutate(c.id)}
              disabled={markRead.isPending}
            >
              <Check size={14} className="mr-1 inline" /> Oznacz jako przeczytane
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
